import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getQueryFn } from "@/lib/queryClient";
import { PageHeader } from "@/components/shared/page-header";
import { DataTable, type ColumnDef } from "@/components/shared/data-table";
import { formatDate } from "@/lib/format";
import { ListTodo } from "lucide-react";
import type { Project, ProjectTask } from "@db/schema";

type PortalTask = ProjectTask & {
  project: Project;
  assignee: { firstName: string | null; lastName: string | null; email: string } | null;
};

const columns: ColumnDef<PortalTask>[] = [
  {
    key: "title",
    header: "Task",
    cell: (row) => (
      <div>
        <div className="font-medium">{row.title}</div>
        <div className="text-xs text-muted-foreground">{row.project.name}</div>
      </div>
    ),
  },
  {
    key: "status",
    header: "Status",
    cell: (row) => row.status.replace(/_/g, " "),
  },
  {
    key: "assignee",
    header: "Assignee",
    cell: (row) =>
      row.assignee
        ? `${row.assignee.firstName || ""} ${row.assignee.lastName || ""}`.trim() ||
          row.assignee.email
        : "Unassigned",
  },
  {
    key: "dueDate",
    header: "Due",
    cell: (row) => (row.dueDate ? formatDate(row.dueDate) : "TBD"),
  },
];

export default function ClientTasks() {
  const [search, setSearch] = useState("");

  const { data, isLoading } = useQuery<PortalTask[]>({
    queryKey: ["/api/portal/tasks"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const filtered = data?.filter((task) => {
    if (!search) return true;
    const query = search.toLowerCase();
    return (
      task.title.toLowerCase().includes(query) ||
      task.project.name.toLowerCase().includes(query)
    );
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tasks"
        description="Read-only task visibility for delivery items shared with your account."
      />

      <DataTable
        columns={columns}
        data={filtered}
        isLoading={isLoading}
        rowKey={(row) => row.id}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search tasks..."
        emptyIcon={ListTodo}
        emptyTitle="No visible tasks"
        emptyDescription="Client-visible tasks will appear here."
      />
    </div>
  );
}
